import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import {
  filterSubscriptionCustomers,
  legacyCustomerApi,
  subscriptionAdminApi,
  getApiErrorMessage,
} from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';

const OFFER_TYPES = ['percent_off', 'fixed_off', 'free_months'];

const emptyOffer = () => ({
  code: '',
  name: '',
  type: 'percent_off',
  value: '',
  maxRedemptions: '',
  validFrom: '',
  validTo: '',
  status: 'active',
});

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [form, setForm] = useState(emptyOffer());
  const [assignForm, setAssignForm] = useState({ offerId: '', userId: '', contractId: '' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });
  const canEdit = canWrite('offers');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [offerData, contractData, userData] = await Promise.all([
        subscriptionAdminApi.listOffers(),
        subscriptionAdminApi.listContracts(),
        legacyCustomerApi.listUsers(),
      ]);
      setOffers(Array.isArray(offerData) ? offerData : []);
      setContracts(Array.isArray(contractData) ? contractData : []);
      setCustomers(filterSubscriptionCustomers(userData));
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveOffer = async () => {
    setSaving(true);
    try {
      await subscriptionAdminApi.createOffer({
        code: form.code.trim(),
        name: form.name.trim(),
        type: form.type,
        value: Number(form.value),
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : undefined,
        validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : undefined,
        validTo: form.validTo ? new Date(form.validTo).toISOString() : undefined,
        applicablePlanIds: [],
        status: form.status,
      });
      setSnackbar({ open: true, type: 'success', message: 'Offer created.' });
      setDialogOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const assignOffer = async () => {
    if (!assignForm.offerId || !assignForm.userId) {
      setSnackbar({ open: true, type: 'error', message: 'Offer and customer are required.' });
      return;
    }
    setSaving(true);
    try {
      await subscriptionAdminApi.assignOffer(assignForm.offerId, {
        userId: assignForm.userId,
        ...(assignForm.contractId ? { contractId: assignForm.contractId } : {}),
      });
      setSnackbar({ open: true, type: 'success', message: 'Offer assigned.' });
      setAssignOpen(false);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell
      title="Step 3 — Offers"
      subtitle="GET/POST /offers · POST /offers/:id/assign (optional before billing)"
      actions={
        canEdit ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => { setAssignForm({ offerId: '', userId: '', contractId: '' }); setAssignOpen(true); }}>
              Assign offer
            </Button>
            <Button variant="contained" onClick={() => { setForm(emptyOffer()); setDialogOpen(true); }}>
              New offer
            </Button>
          </Box>
        ) : null
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>Optional step — assign before submit/approve if the offer affects contract billing.</Alert>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.map((o) => (
                <TableRow key={o._id}>
                  <TableCell>{o.code}</TableCell>
                  <TableCell>{o.name}</TableCell>
                  <TableCell>{o.type}</TableCell>
                  <TableCell>{o.value}</TableCell>
                  <TableCell><Chip label={o.status} size="small" /></TableCell>
                </TableRow>
              ))}
              {!offers.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b' }}>
                    No offers yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create offer</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {OFFER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField label="Value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <TextField label="Max redemptions" type="number" value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} />
          <TextField label="Valid from" type="datetime-local" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField label="Valid to" type="datetime-local" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveOffer} disabled={saving}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign offer</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField select label="Offer" value={assignForm.offerId} onChange={(e) => setAssignForm({ ...assignForm, offerId: e.target.value })}>
            <MenuItem value="">Select…</MenuItem>
            {offers.map((o) => (
              <MenuItem key={o._id} value={o._id}>{o.code} — {o.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Customer" value={assignForm.userId} onChange={(e) => setAssignForm({ ...assignForm, userId: e.target.value })}>
            <MenuItem value="">Select…</MenuItem>
            {customers.map((u) => (
              <MenuItem key={u._id} value={u._id}>{u.email}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Contract (optional)" value={assignForm.contractId} onChange={(e) => setAssignForm({ ...assignForm, contractId: e.target.value })}>
            <MenuItem value="">None</MenuItem>
            {contracts
              .filter((c) => !assignForm.userId || String(c.userId) === String(assignForm.userId))
              .map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {String(c._id).slice(-8)} — {c.status}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={assignOffer} disabled={saving}>Assign</Button>
        </DialogActions>
      </Dialog>

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
